import React, { Component } from 'react'
import Select from "react-select";
import { sweetalert } from "../../App";
import { buildRow, getLabel, getLabelsForMultiSelect, toLocalDateTime, resolveEducationSiteName, hideModal, parseDate } from "../../util/FormatUtil";
import { AiOutlineDown, AiOutlineUp, AiOutlineCloudDownload } from "react-icons/ai";
import Overlay from "../Overlay";
import SystemAPI from "../../network/SystemAPI";
import PhoneInput from 'react-phone-number-input';
import SamplesAPI from '../../network/SamplesAPI';
import ExemptionAPI from '../../network/ExemptionAPI';
import { withTranslation } from 'react-i18next';
import Validator from "../../validation/Validator";
import { Validators } from "../../validation/Validators";


interface ExemptionModalState {
  selectedExemption
  customFieldData?
  ethnicities?
  genders?
  races?
  immunizations?
  exemptionArrowDown?
  childArrowDown?
  parentArrowDown?
  schoolArrowDown?
  fileUploadArrowDown?
  gradeOptions?
  showLoading?
  showInBetween?
  relationships?
  exemptionTypes?
  districts?
  schools?
  facilitySchools?
  facilityChildcares?
  EducationSite?
  schoolOrChildCare?
  childCareFacilities?
  exemptionReasons?
}
interface ExemptionModalProps {
  selectedExemption
  immunizations
  t
  onClose
  onSave?: () => void
  states
}


class ExemptionRecordModal extends Component<ExemptionModalProps, ExemptionModalState> {
  public static ID = "ExemptionRecordModal";
  private nextSiteKey = 0;

  constructor(props) {
    super(props);

    this.state = {
      selectedExemption: {},
      gradeOptions: [
        { label: "Headstart", value: "Headstart" },
        { label: "Pre-K", value: "Pre-K" },
        { label: "Kindergarten", value: "Kindergarten" },
        { label: "1st", value: "1st" },
        { label: "2nd", value: "2nd" },
        { label: "3rd", value: "3rd" },
        { label: "4th", value: "4th" },
        { label: "5th", value: "5th" },
        { label: "6th", value: "6th" },
        { label: "7th", value: "7th" },
        { label: "8th", value: "8th" },
        { label: "9th", value: "9th" },
        { label: "10th", value: "10th" },
        { label: "11th", value: "11th" },
        { label: "12th", value: "12th" }
      ]
    };

  }

  // Returns {label, value} for react-select, resolving from facility or legacy lists
  resolveSchoolLabel(schoolId, isFacilityID?) {
    if (!schoolId) return null;
    let legacyFormatted = (this.state.schools || []).map(s => ({ label: s.SchoolName || s.label, value: s.ID || s.value }));
    let name = resolveEducationSiteName({ School: schoolId, isFacilityID }, 'School', this.state.facilitySchools, legacyFormatted);
    return name ? { label: name, value: schoolId } : null;
  }

  resolveChildcareLabel(childcareId, isFacilityID?) {
    if (!childcareId) return null;
    let name = resolveEducationSiteName({ ChildCareSite: childcareId, isFacilityID }, 'ChildCareSite', this.state.facilityChildcares, this.state.childCareFacilities);
    return name ? { label: name, value: childcareId } : null;
  }

  hasSchoolEntry() {
    return (this.state.selectedExemption?.EducationSite || []).some(s => s.schoolOrChildCare === 0);
  }

  hasChildCareEntry() {
    return (this.state.selectedExemption?.EducationSite || []).some(s => s.schoolOrChildCare === 1);
  }

  canAddMore() {
    return !this.hasSchoolEntry() || !this.hasChildCareEntry();
  }

  handleAddEducationSite() {
    if (!this.canAddMore()) return;
    this.setState((prevState) => ({
      selectedExemption: {
        ...prevState.selectedExemption,
        EducationSite: [
          ...(prevState.selectedExemption?.EducationSite || []),
          { schoolOrChildCare: null, SchoolDistrict: null, School: null, ChildCareSite: null, SchoolGrade: null, SchoolPhone: null, _key: this.nextSiteKey++ }
        ]
      }
    }));
  }

  handleRemoveEducationSite(index) {
    this.setState((prevState) => {
      let schoolArray = [...(prevState.selectedExemption?.EducationSite || [])];
      schoolArray.splice(index, 1);
      return {
        selectedExemption: { ...prevState.selectedExemption, EducationSite: schoolArray }
      };
    });
  }

  componentDidMount(): void {
    SystemAPI.getAllEthnicities().then(data => {
      this.setState({ ethnicities: data })
    })
    SystemAPI.getAllGenders().then(data => {
      this.setState({ genders: data })
    })
    SystemAPI.getAllRaces().then(data => {
      this.setState({ races: data })
    })
    SystemAPI.getAllImmunizations().then(data => {
      this.setState({ immunizations: data })
    })
    SystemAPI.getAllRelationships().then(data => {
      this.setState({ relationships: data })
    })
    SystemAPI.getAllExemptionTypes().then(data => {
      this.setState({ exemptionTypes: data })
    })
    SystemAPI.getAllPersonalExemptionReasons().then(data => {
      this.setState({ exemptionReasons: data })
    })
    SystemAPI.getAllSchoolDistricts().then(data => {
      this.setState({ districts: data })
    })
    // Keep Facility and old Schools lists separate.
    // The isFacilityID flag on each EducationSite entry tells us which list to search.
    SystemAPI.getFacilitySchools().then(data => {
      this.setState({ facilitySchools: data })
    })
    SystemAPI.getAllSchools().then(data => {
      //@ts-ignore
      this.setState({ schools: data.schools.map(s => ({ label: s.SchoolName, value: s.ID })) })
    })
    // Keep Facility and old ChildCareFacilities lists separate.
    // The isFacilityID flag on each EducationSite entry tells us which list to search.
    SystemAPI.getFacilityChildcares().then(data => {
      this.setState({ facilityChildcares: data })
    })
    SystemAPI.getAllChildCareFacilities().then(data => {
      //@ts-ignore
      this.setState({ childCareFacilities: data.facilities.map(f => ({ label: f.ChildCareSiteName, value: f.ID })) })
    })
  }


  componentWillReceiveProps(nextProps: Readonly<ExemptionModalProps>, nextContext: any): void {
    let parsedSites = this.props.selectedExemption && this.props.selectedExemption.EducationSite
      ? JSON.parse(this.props.selectedExemption.EducationSite)
      : null;
    if (Array.isArray(parsedSites)) {
      parsedSites = parsedSites.map(s => ({ ...s, _key: this.nextSiteKey++ }));
    }
    this.setState({
      selectedExemption: {
        ...this.props.selectedExemption,
        fileNames: this.props.selectedExemption && this.props.selectedExemption.FileUploadPaths ? this.getFileNames(this.props.selectedExemption.FileUploadPaths) : [],
        ChildRaceID: this.props.selectedExemption && this.props.selectedExemption.ChildRaceID ? JSON.parse(this.props.selectedExemption.ChildRaceID) : null,
        EducationSite: parsedSites
      }
    })
  }

  getFileNames(urlArray) {
    let parsedArray = Array.from(JSON.parse(urlArray));
    let namesOnly = [];
    parsedArray.map(f => {
      //@ts-ignore
      let temp = f.split('/').pop();
      namesOnly.push(temp)
    })
    return namesOnly

  }

  getRacesList() {
    if (this.state.selectedExemption?.ChildRaceID?.length < 2) {
      return getLabel(parseInt(this.state.selectedExemption?.ChildRaceID), this.state.races, this.props.t)?.label
    } else {
      let raceArray = this.state.selectedExemption?.ChildRaceID;
      let raceStringArray = [];
      for (let val of raceArray) {
        raceStringArray.push(getLabel(parseInt(val), this.state.races, this.props.t).label);
      }
      let raceString = raceStringArray.join(", ");
      return raceString;
    }
  }


  downloadFile = async (name) => {
    this.setState({ showInBetween: true })
    let file = await SamplesAPI.downloadFilesFromCloud({ ReqNum: this.state.selectedExemption?.ReqNum, fileName: name })
    try {
      if (typeof file === "object") {
        sweetalert.fire({
          icon: "error",
          title: "",
          text: this.props.t("File not found"),
        });
        this.setState({ showInBetween: false });
        return;
      } else {
        const link = document.createElement("a");
        link.href = file;
        link.download = `${name}`;
        link.click();
        this.setState({ showInBetween: false })
      }
    } catch (error) {
      console.error('error downloading file', error)
      this.setState({ showInBetween: false })
      return sweetalert.fire({ icon: 'error', title: '', text: this.props.t('Could not download file. Please try again.') })
    }
  }


  // Mirrors SchoolInfoModal.validateSchool so edit-time rules match submission-time rules
  validateEducationSite(site: any): { success: boolean; error?: string } {
    if (site.schoolOrChildCare !== 0 && site.schoolOrChildCare !== 1) {
      return { success: false, error: this.props.t("School/Child Care cannot be empty.") };
    }

    const formData = {
      ChildCareSite: site.ChildCareSite,
      School: site.School,
      District: site.SchoolDistrict,
      Grade: site.SchoolGrade,
      OtherChildCareSite: site.OtherChildCareSite
    };

    let validator = new Validator<any>();
    if (site.schoolOrChildCare === 0) {
      validator = validator
        .withSimpleValidation("Grade", Validators.requireNonNullValidator())
        .withSimpleValidation("School", Validators.requireNonNullValidator())
        .withSimpleValidation("District", Validators.requireNonNullValidator());
    } else if (site.schoolOrChildCare === 1) {
      validator = validator.withSimpleValidation("ChildCareSite", Validators.requireNonNullValidator(this.props.t("Child Care Site")));
      if (site.ChildCareSite === -1) {
        validator = validator.withSimpleValidation("OtherChildCareSite", Validators.requireNonNullValidator(this.props.t("Other Child Care Site")));
      }
    }

    const validationResponse = validator.validate(formData);
    if (!validationResponse.success) {
      return { success: false, error: this.props.t(validationResponse.error) };
    }
    return { success: true };
  }

  saveEdits = async () => {
    const sites = this.state.selectedExemption?.EducationSite || [];
    for (const site of sites) {
      const siteValidation = this.validateEducationSite(site);
      if (!siteValidation.success) {
        return sweetalert.fire({ icon: "error", title: "", text: siteValidation.error });
      }
    }

    // Validate DOB the same way the provider portal does
    const dobValidation = {
      ChildDOB: this.state.selectedExemption?.ChildDOB
        ? parseDate(new Date(this.state.selectedExemption.ChildDOB))
        : null
    };
    let validator = new Validator<any>()
      .withValidation("ChildDOB", Validators.requireSchoolAgeDOB(this.props.t("Child Date of birth")));
    let validationResult = validator.validate(dobValidation);
    if (!validationResult.success) {
      return sweetalert.fire({ icon: "error", title: "", text: this.props.t(validationResult.error) });
    }

    this.setState({ showLoading: true });
    try {
      const childDOB = this.state.selectedExemption?.ChildDOB
        ? parseDate(new Date(this.state.selectedExemption.ChildDOB), true)
        : undefined;

      const result = await ExemptionAPI.editExemption(
        this.state.selectedExemption.ID,
        this.state.selectedExemption.EducationSite,
        childDOB
      );
      if (result.success) {
        sweetalert.fire({ icon: "success", title: "", text: this.props.t("Exemption updated successfully.") });
        hideModal(ExemptionRecordModal.ID);
        this.props.onClose();
        if (this.props.onSave) this.props.onSave();
      } else {
        sweetalert.fire({ icon: "error", title: "", text: this.props.t(result.reason || "Failed to update exemption.") });
      }
    } catch (e) {
      console.error("Error saving exemption edits:", e);
      sweetalert.fire({ icon: "error", title: "", text: this.props.t("An error occurred while saving.") });
    } finally {
      this.setState({ showLoading: false });
    }
  }

  handleUpdateSchool(index, key, value) {
    this.setState((prevState) => {
      let schoolArray = (prevState.selectedExemption.EducationSite || []).map(s => ({ ...s }));
      schoolArray[index][key] = value;

      // Mark facility-sourced selections so the correct list is used for name resolution.
      // Reset to null when the field is cleared so stale flags don't mislead lookups.
      if (key === "School" || key === "ChildCareSite") {
        schoolArray[index].isFacilityID = (value && value !== -1) ? true : null;
      }

      // Auto-populate district from the selected school's facility record
      if (!schoolArray[index].SchoolDistrict && key === "School" && value !== -1) {
        let facilityMatch = (this.state.facilitySchools || []).find((f) => f.ID === value);
        schoolArray[index].SchoolDistrict = facilityMatch?.SchoolDistrictID || null;
      }
      // Clear school when district changes since schools are filtered by district
      if (key === "SchoolDistrict" && schoolArray[index].School !== -1) {
        schoolArray[index].School = null;
      }

      return {
        selectedExemption: {
          ...prevState.selectedExemption,
          EducationSite: schoolArray,
        }
      };
    });
  }

  render() {

    let translationExemptionTypes = this.state.exemptionTypes && this.state.exemptionTypes.length > 0 ? this.state.exemptionTypes.map(e => { return { label: this.props.t(e.label), value: e.value } }) : [];

    return (
      <div
        className="modal fade form_modal" id={ExemptionRecordModal.ID} tabIndex={-1} role="dialog" aria-labelledby="result_modal_label" aria-hidden="true">
        <Overlay show_loading={this.state.showLoading} zIndex={10005} />
        <div className="modal-dialog modal-lg modal-xl col-8 px-0" role="document">
          <div className="modal-content">
            <div className="container-fluid" >
              <div className="modal-header">
                <h5 className="modal-title" id="result_modal_label">{this.props.t("Exemption Record")}</h5>
                <button type="button" className="close" data-dismiss="modal" onClick={() => this.props.onClose()} aria-label={this.props.t("Close")}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body p-0">
                <section className='my-2 mx-auto col-11'>
                  {buildRow(this.props.t("Status"),
                    <input className={"form-control col-12 mx-auto"}
                      maxLength={50}
                      readOnly={this.state.selectedExemption?.ID != null}
                      autoComplete={"off"}
                      type={"search"}
                      name={"Result"}
                      aria-label={this.props.t('Status')}
                      value={this.state.selectedExemption?.Status && new Date() > new Date(this.state.selectedExemption?.PatientNotifiedDate) ? this.props.t("Approved") : this.state.selectedExemption?.Status == null || new Date() < new Date(this.state.selectedExemption?.PatientNotifiedDate) ? this.props.t("Pending") : this.props.t("Denied")}
                    />
                  )}
                  {
                    this.state.selectedExemption?.ExemptionStatus?.value == "2" ?
                      buildRow(this.props.t(`Reason for Denial`),
                        <input
                          id="StatusDate"
                          min={'1000-01-01'}
                          max={'9999-12-31'}
                          className={"form-control col-12 mx-auto"}
                          autoComplete={"off"}
                          type={"search"}
                          readOnly={true}
                          name={"Reason for Denial"}
                          aria-label={this.props.t("Reason for Denial")}
                          value={this.props.t(this.state.selectedExemption?.DenialReason)}
                        />
                      )
                      : <></>}
                  {buildRow(this.props.t("Confirmation Code"),
                    <input className={"form-control col-12 mx-auto"}
                      maxLength={50}
                      readOnly={this.state.selectedExemption?.ID != null}
                      autoComplete={"off"}
                      type={"search"}
                      name={"Result"}
                      aria-label={this.props.t('Confirmation Code')}
                      value={this.state.selectedExemption?.ReqNum}
                    />
                  )}
                </section>
                <div className="card">
                  <button className="card-header verlag-bold accordionButton w-100" data-toggle="collapse" data-target="#exemptionXLCollapse" onClick={() => this.setState({ exemptionArrowDown: !this.state.exemptionArrowDown, })} aria-expanded="false" aria-controls="exemptionXLCollapse" >
                    <div className="row justify-content-between px-3">
                      <div className="">
                        <h4>{this.props.t("Exemption")}</h4>
                      </div>
                      <div className="col-2">
                        {this.state.exemptionArrowDown ? (
                          <AiOutlineUp className={"float-right"} size={30} data-toggle="collapse" data-target="#exemptionXLCollapse" role="button" aria-expanded="false" aria-controls="exemptionXLCollapse" />
                        ) : (
                          <AiOutlineDown className={"float-right"} size={30} data-toggle="collapse" data-target="#exemptionXLCollapse" role="button" aria-expanded="false" aria-controls="exemptionXLCollapse" />
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="card-body collapse" id={"exemptionXLCollapse"} >
                    <div className="row">
                      <div className="col-12 col-xl-6">
                        {buildRow(this.props.t("Exemption Type"),
                          <Select
                            id='ExemptionType'
                            isSearchable={true}
                            placeholder={<div className="accessibilityText">Please Select...</div>}
                            noOptionsMessage={() => this.props.t("No option")}
                            isDisabled={this.state.selectedExemption?.ID != null}
                            value={getLabel(this.state.selectedExemption.ExemptionType, this.state.exemptionTypes, this.props.t)}
                            onChange={(e) => this.setState((prevState) => ({
                              selectedExemption: {
                                ...prevState.selectedExemption,
                                ExemptionType: e.value
                              }
                            }))}
                            className={"state_select"}
                            aria-label={this.props.t('Exemption Type')}
                            options={translationExemptionTypes}
                          />
                        )}
                        {buildRow(this.props.t("Immunizations for Exemption"),
                          <Select
                            id='ExemptImmunizations'
                            isSearchable={true}
                            isMulti={true}
                            placeholder={<div className="accessibilityText">{this.props.t("Please Select...")}</div>}
                            noOptionsMessage={() => this.props.t("No option")}
                            isDisabled={this.state.selectedExemption?.ID != null}
                            value={this.state.selectedExemption.ExemptImmunizations ? getLabelsForMultiSelect(JSON.parse(this.state.selectedExemption.ExemptImmunizations), this.state.immunizations) : null}
                            className={"state_select"}
                            aria-label={this.props.t('Immunizations for Exemption')}
                            options={translationExemptionTypes}
                          />, "Immunizations for Exemption"
                        )}
                        {this.state.selectedExemption?.ExemptionType === 5 ?
                          <>
                            {buildRow(this.props.t("Child Harmful Health Condition"),
                              <input
                                className={"form-control"}
                                maxLength={50}
                                readOnly={this.state.selectedExemption?.ID != null}
                                autoComplete={"off"}
                                type={"search"}
                                name={"Result"}
                                aria-label={this.props.t('Child Harmful Health Condition')}
                                value={this.props.t(this.state.selectedExemption?.HarmfulCondition)}
                              />
                            )}
                          </> : <></>
                        }
                      </div>
                      <div className="col-12 col-xl-6">
                        {this.state.selectedExemption?.ExemptionType === 5 ?
                          <>

                          </>
                          :
                          this.state.selectedExemption?.ExemptionType === 2 ?
                            <>
                            </>
                            :
                            this.state.selectedExemption?.ExemptionType === 1 ?
                              <>
                                {buildRow(this.props.t("Reason"),
                                  <Select
                                    isSearchable={true}
                                    placeholder={<div className="accessibilityText">{this.props.t("Please Select...")}</div>}
                                    noOptionsMessage={() => this.props.t("No option")}
                                    isDisabled={this.state.selectedExemption?.ID != null}
                                    value={this.state.selectedExemption.PersonalExemptionReason ? getLabel(this.state.selectedExemption.PersonalExemptionReason, this.state.exemptionReasons, this.props.t) : null}
                                    className={"state_select"}
                                    aria-label={this.props.t('Reason')}
                                    options={this.state.exemptionReasons}
                                  />
                                )}
                                {this.state.selectedExemption.PersonalExemptionReason === 7 ? buildRow(this.props.t("Summary of Objection"),
                                  <div>
                                    <textarea
                                      className={"form-control mr-3"}
                                      maxLength={600}
                                      autoComplete={"off"}
                                      name={"SummaryOfObjection"}
                                      disabled={this.state.selectedExemption?.ID != null}
                                      value={this.state.selectedExemption?.ObjectionSummary}
                                      aria-label={this.props.t("Summary of Objection")}
                                    />
                                    <div className="row pt-1 pr-3 justify-content-end">
                                      <div className={this.state.selectedExemption?.ObjectionSummary && this.state.selectedExemption?.ObjectionSummary.length > 0 ? 'visible' : 'invisible'}>
                                        <div style={{ fontSize: '0.8em' }}>{this.state.selectedExemption?.ObjectionSummary && this.state.selectedExemption?.ObjectionSummary.length}/600</div>
                                      </div>
                                    </div>
                                  </div>
                                ) : <></>}
                              </> : <></>}
                        {buildRow(this.props.t("Created Date"),
                          <input className={"form-control"}
                            maxLength={50}
                            readOnly={this.state.selectedExemption?.ID != null}
                            autoComplete={"off"}
                            type={"search"}
                            name={"CreatedDate"}
                            aria-label={this.props.t("Created Date")}
                            value={this.state.selectedExemption?.CreatedDate ? toLocalDateTime(this.state.selectedExemption?.CreatedDate) : null}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/*Physician */}
                {this.state.selectedExemption?.ExemptionType === 5 && <div className="card">
                  <button className="card-header verlag-bold accordionButton w-100" data-toggle="collapse" data-target="#fileUploadXLCollapse" onClick={() => this.setState({ fileUploadArrowDown: !this.state.fileUploadArrowDown })} aria-expanded="false" aria-controls="fileUploadXLCollapse" >
                    <div className="row justify-content-between px-3">
                      <div className="">
                        <h4>{this.props.t("Physician")}</h4>
                      </div>
                      <div className="col-2">
                        {this.state.fileUploadArrowDown ? (
                          <AiOutlineUp className={"float-right"} size={30} data-toggle="collapse" data-target="#fileUploadXLCollapse" role="button" aria-expanded="false" aria-controls="fileUploadXLCollapse" />
                        ) : (
                          <AiOutlineDown className={"float-right"} size={30} data-toggle="collapse" data-target="#fileUploadXLCollapse" role="button" aria-expanded="false" aria-controls="fileUploadXLCollapse" />
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="card-body collapse" id={"fileUploadXLCollapse"} >
                    {(
                      <section className="row">
                        <section className="col-12 col-xl-6">
                          {buildRow(this.props.t("Name"),
                            <input
                              className={"form-control"}
                              maxLength={50}
                              readOnly={this.state.selectedExemption?.ID != null}
                              autoComplete={"off"}
                              type={"search"}
                              name={"Result"}
                              aria-label={this.props.t('Physician Name')}
                              value={`${this.state.selectedExemption?.PhysicianFirstName} ${this.state.selectedExemption?.PhysicianLastName}`}
                            />
                          )}
                          {buildRow(this.props.t('Street Address'),
                            <input
                              id={'StreetAddress'}
                              className={"form-control"}
                              maxLength={50}
                              readOnly={true}
                              autoComplete={"off"}
                              type={"search"}
                              name={'StreetAddress'}
                              aria-label={this.props.t("Street Address")}
                              onChange={(e) => {
                                this.setState((prevState) => ({
                                  selectedExemption: {
                                    ...prevState.selectedExemption,
                                    PhysicianAddress: e.target.value
                                  }
                                }))
                              }}
                              value={this.state.selectedExemption?.PhysicianAddress}
                            />, this.props.t('Street Address'), true
                          )}
                          {buildRow(this.props.t('City'),
                            <input
                              id={'SecondaryAddressLine'}
                              className={"form-control"}
                              maxLength={50}
                              readOnly={true}
                              autoComplete={"off"}
                              type={"search"}
                              name={'City'}
                              aria-label={this.props.t("City")}
                              onChange={(e) => {
                                this.setState((prevState) => ({
                                  selectedExemption: {
                                    ...prevState.selectedExemption,
                                    PhysicianCity: e.target.value
                                  }
                                }))
                              }}
                              value={this.state.selectedExemption?.PhysicianCity}
                            />, this.props.t('City'), true
                          )}
                          {buildRow(this.props.t('State'),
                            <Select
                              key={'State'}
                              id={'State'}
                              isSearchable={true}
                              readOnly={true}
                              isDisabled={true}
                              placeholder={<div className="accessibilityText">{this.props.t("Please Select...")}</div>}
                              noOptionsMessage={() => this.props.t("No option")}
                              aria-label={this.props.t("State")}
                              value={this.state.selectedExemption ? getLabel(this.state.selectedExemption.PhysicianState, this.props.states, this.props.t) : null}
                              onChange={(e) => {
                                this.setState((prevState) => ({
                                  selectedExemption: {
                                    ...prevState.selectedExemption,
                                    PhysicianState: e.value
                                  }
                                }))
                              }}
                              className={'state_select'}
                              options={this.props.states}
                            />, this.props.t('State')
                            , true
                          )}

                        </section>
                        <section className="col-12 col-xl-6">
                          {buildRow(this.props.t('Zipcode'),
                            <input
                              id={'Zipcode'}
                              className={"form-control"}
                              maxLength={50}
                              readOnly={true}
                              autoComplete={"off"}
                              type={"search"}
                              name={'Zipcode'}
                              aria-label={this.props.t("Zipcode")}
                              onChange={(e) => {
                                this.setState((prevState) => ({
                                  selectedExemption: {
                                    ...prevState.selectedExemption,
                                    PhysicianZip: e.target.value
                                  }
                                }))
                              }}
                              value={this.state.selectedExemption?.PhysicianZip}
                            />, this.props.t('Zipcode'), true
                          )}
                          {buildRow(this.props.t('Apartment/Suite/Building #'),
                            <input
                              id={'SecondaryAddressLine'}
                              className={"form-control"}
                              maxLength={50}
                              readOnly={true}
                              autoComplete={"off"}
                              type={"search"}
                              name={'SecondaryAddressLine'}
                              aria-label={this.props.t("Apartment/Suite/Building #")}
                              onChange={(e) => {
                                this.setState((prevState) => ({
                                  selectedExemption: {
                                    ...prevState.selectedExemption,
                                    PhysicianAdditionalAddressInfo: e.target.value
                                  }
                                }))
                              }}
                              value={this.state.selectedExemption?.PhysicianAdditionalAddressInfo}
                            />, this.props.t('Apartment/Suite/Building #')
                          )}
                          {buildRow(this.props.t("Office Phone"),
                            <input
                              className={"form-control"}
                              maxLength={50}
                              readOnly={this.state.selectedExemption?.ID != null}
                              autoComplete={"off"}
                              type={"search"}
                              name={"Result"}
                              aria-label={this.props.t("Physician Phone")}
                              value={this.state.selectedExemption?.PhysicianPhone}
                            />
                          )}
                          {buildRow(this.props.t("Email"),
                            <input
                              className={"form-control"}
                              maxLength={50}
                              readOnly={this.state.selectedExemption?.ID != null}
                              autoComplete={"off"}
                              type={"search"}
                              name={"Result"}
                              aria-label={this.props.t("Physician Email")}
                              value={this.state.selectedExemption?.PhysicianEmail}
                            />
                          )}
                          {this.state.selectedExemption?.fileNames && this.state.selectedExemption?.fileNames.length > 0 && this.state.selectedExemption?.fileNames.map((fileName, index) => (
                            <div className="form-group row">
                              <label htmlFor={`SelectedFile${index}`} className="col-12 col-sm-4 col-form-label text-center text-md-left">{this.props.t("Selected File")} #{index + 1}</label>
                              <div className="col-8 col-sm-6 p-0 pt-2 m-0 text-center text-md-left" id={`SelectedFile${index}`}>
                                {fileName}
                              </div>
                              <div className="col-2 col-sm-1">
                                <AiOutlineCloudDownload size={30} onClick={() => this.downloadFile(fileName)} />
                              </div>

                            </div>
                          ))}
                        </section>
                      </section>
                    )
                    }
                  </div>
                </div>}
                {/*Child */}
                <div className="card">
                  <button className="card-header verlag-bold accordionButton w-100" data-toggle="collapse" data-target="#childXLCollapse" onClick={() => this.setState({ childArrowDown: !this.state.childArrowDown, })} aria-expanded="false" aria-controls="childXLCollapse">
                    <div className="row justify-content-between px-3">
                      <div className="">
                        <h4>{this.props.t("Child")}</h4>
                      </div>
                      <div className="col-2">
                        {this.state.childArrowDown ? (
                          <AiOutlineUp className={"float-right"} size={30} data-toggle="collapse" data-target="#childXLCollapse" role="button" aria-expanded="false" aria-controls="childXLCollapse" />
                        ) : (
                          <AiOutlineDown className={"float-right"} size={30} data-toggle="collapse" data-target="#childXLCollapse" role="button" aria-expanded="false" aria-controls="childXLCollapse" />
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="card-body collapse" id={"childXLCollapse"}>
                    <div className="row">
                      <div className="col-12 col-xl-6">
                        {buildRow(this.props.t("Name"),
                          <input
                            className={"form-control"}
                            maxLength={50}
                            readOnly={this.state.selectedExemption?.ID != null}
                            autoComplete={"off"}
                            type={"search"}
                            name={"Result"}
                            aria-label={this.props.t("Child Name")}
                            onChange={(e) => {
                              this.setState((prevState) => ({
                                selectedExemption: {
                                  ...prevState.selectedExemption,
                                  Value: e.target.value
                                }
                              }))
                            }}
                            value={this.state.selectedExemption?.ChildMiddleInitial ? `${this.state.selectedExemption?.ChildFirstName} ${this.state.selectedExemption?.ChildMiddleInitial} ${this.state.selectedExemption?.ChildLastName}` : `${this.state.selectedExemption?.ChildFirstName} ${this.state.selectedExemption?.ChildLastName}`}
                          />
                        )}
                        {buildRow(this.props.t("Child's Mother's Maiden Name"),
                          <input
                            className={"form-control"}
                            maxLength={50}
                            readOnly={this.state.selectedExemption?.ID != null}
                            autoComplete={"off"}
                            type={"search"}
                            name={"Result"}
                            aria-label={this.props.t("Child's Mother's Maiden Name")}
                            value={this.state.selectedExemption?.MotherMaidenName}
                          />
                        )}
                        {buildRow(this.props.t("Date of Birth"),
                          <input
                            min={'1000-01-01'}
                            max={new Date().toISOString().split('T')[0]}
                            className={"form-control"}
                            autoComplete={"off"}
                            type={"date"}
                            name={"ChildDOB"}
                            aria-label={this.props.t("Child Date of Birth")}
                            value={this.state.selectedExemption?.ChildDOB ? new Date(this.state.selectedExemption.ChildDOB).toISOString().split('T')[0] : ''}
                            onChange={(e) => {
                              if (Date.parse(e.target.value)) {
                                this.setState((prevState) => ({
                                  selectedExemption: {
                                    ...prevState.selectedExemption,
                                    ChildDOB: new Date(e.target.value)
                                  }
                                }))
                              }
                            }}
                          />, this.props.t("Child DOB"), true
                        )}
                        {buildRow(this.props.t("Birth Location"),
                          <input
                            className={"form-control"}
                            maxLength={50}
                            readOnly={this.state.selectedExemption?.ID != null}
                            autoComplete={"off"}
                            type={"search"}
                            name={"Result"}
                            aria-label={this.props.t("Child Birth Location")}
                            value={this.state.selectedExemption?.ChildBirthCountry === "United States" ? `${this.state.selectedExemption?.ChildBirthState}, ${this.state.selectedExemption?.ChildBirthCountry}` : `${this.state.selectedExemption?.ChildBirthCountry}`}
                          />
                        )}
                        {buildRow(this.props.t("Gender"),
                          <input
                            className={"form-control"}
                            maxLength={50}
                            readOnly={this.state.selectedExemption?.ID != null}
                            autoComplete={"off"}
                            type={"search"}
                            name={"Result"}
                            aria-label={this.props.t("Child Gender")}
                            value={getLabel(this.state.selectedExemption?.ChildGenderID, this.state.genders, this.props.t)?.label}
                          />
                        )}
                      </div>
                      <div className="col-12 col-xl-6">
                        {buildRow(this.props.t("Race"),
                          <input
                            className={"form-control"}
                            maxLength={50}
                            readOnly={this.state.selectedExemption?.ID != null}
                            autoComplete={"off"}
                            type={"search"}
                            name={"Result"}
                            aria-label={this.props.t("Child Race")}
                            value={this.state.selectedExemption?.ChildRaceID ? this.getRacesList() : null}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/*Parent/ Guardian */}
                <div className="card">
                  <button className="card-header verlag-bold accordionButton w-100" data-toggle="collapse" data-target="#parentXLCollapse" onClick={() => this.setState({ parentArrowDown: !this.state.parentArrowDown, })} aria-expanded="false" aria-controls="parentXLCollapse">
                    <div className="row justify-content-between px-3">
                      <div className="">
                        <h4>{this.props.t("Parent/ Guardian")}</h4>
                      </div>
                      <div className="col-2">
                        {this.state.parentArrowDown ? (
                          <AiOutlineUp className={"float-right"} size={30} data-toggle="collapse" data-target="#parentXLCollapse" role="button" aria-expanded="false" aria-controls="parentXLCollapse" />
                        ) : (
                          <AiOutlineDown className={"float-right"} size={30} data-toggle="collapse" data-target="#parentXLCollapse" role="button" aria-expanded="false" aria-controls="parentXLCollapse" />
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="card-body collapse" id={"parentXLCollapse"}>
                    <div className="row">
                      <div className="col-12 col-xl-6">
                        {buildRow(this.props.t("Name"),
                          <input
                            className={"form-control"}
                            maxLength={50}
                            readOnly={this.state.selectedExemption?.ID != null}
                            autoComplete={"off"}
                            type={"search"}
                            name={"Result"}
                            aria-label={this.props.t("Parent/Guardian Name")}
                            value={this.state.selectedExemption?.ParentMiddleInitial ? `${this.state.selectedExemption?.ParentFirstName} ${this.state.selectedExemption?.ParentMiddleInitial} ${this.state.selectedExemption?.ParentLastName}` : `${this.state.selectedExemption?.ParentFirstName} ${this.state.selectedExemption?.ParentLastName}`}
                          />
                        )}
                        {buildRow(this.props.t("Relationship"),
                          <input
                            className={"form-control"}
                            maxLength={50}
                            readOnly={this.state.selectedExemption?.ID != null}
                            autoComplete={"off"}
                            type={"search"}
                            name={"Result"}
                            aria-label={this.props.t("Parent/Guardian Relationship")}
                            value={this.state.selectedExemption ? getLabel(this.state.selectedExemption.RelationshipID, this.state.relationships, this.props.t)?.label : null}
                          />
                        )}
                        {buildRow(this.props.t('Street Address'),
                          <input
                            id={'StreetAddress'}
                            className={"form-control"}
                            maxLength={50}
                            readOnly={true}
                            autoComplete={"off"}
                            type={"search"}
                            name={'StreetAddress'}
                            aria-label={this.props.t("Street Address")}
                            onChange={(e) => {
                              this.setState((prevState) => ({
                                selectedExemption: {
                                  ...prevState.selectedExemption,
                                  ParentAddress: e.target.value
                                }
                              }))
                            }}
                            value={this.state.selectedExemption?.ParentAddress}
                          />, this.props.t('Street Address'), true
                        )}
                        {buildRow(this.props.t('City'),
                          <input
                            id={'SecondaryAddressLine'}
                            className={"form-control"}
                            maxLength={50}
                            readOnly={true}
                            autoComplete={"off"}
                            type={"search"}
                            name={'City'}
                            aria-label={this.props.t("City")}
                            onChange={(e) => {
                              this.setState((prevState) => ({
                                selectedExemption: {
                                  ...prevState.selectedExemption,
                                  ParentCity: e.target.value
                                }
                              }))
                            }}
                            value={this.state.selectedExemption?.ParentCity}
                          />, this.props.t('City'), true
                        )}
                        {buildRow(this.props.t('State'),
                          <Select
                            key={'State'}
                            id={'State'}
                            isSearchable={true}
                            readOnly={true}
                            isDisabled={true}
                            placeholder={<div className="accessibilityText">{this.props.t("Please Select...")}</div>}
                            noOptionsMessage={() => this.props.t("No option")}
                            aria-label={this.props.t("State")}
                            value={this.state.selectedExemption ? getLabel(this.state.selectedExemption.ParentState, this.props.states, this.props.t) : null}
                            onChange={(e) => {
                              this.setState((prevState) => ({
                                selectedExemption: {
                                  ...prevState.selectedExemption,
                                  ParentState: e.value
                                }
                              }))
                            }}
                            className={'state_select'}
                            options={this.props.states}
                          />, this.props.t('State')
                          , true
                        )}
                        {buildRow(this.props.t('Zipcode'),
                          <input
                            id={'Zipcode'}
                            className={"form-control"}
                            maxLength={50}
                            readOnly={true}
                            autoComplete={"off"}
                            type={"search"}
                            name={'Zipcode'}
                            aria-label={this.props.t("Zipcode")}
                            onChange={(e) => {
                              this.setState((prevState) => ({
                                selectedExemption: {
                                  ...prevState.selectedExemption,
                                  ParentZip: e.target.value
                                }
                              }))
                            }}
                            value={this.state.selectedExemption?.ParentZip}
                          />, this.props.t('Zipcode'), true
                        )}

                      </div>
                      <div className="col-12 col-xl-6">

                        {buildRow(this.props.t('County'),
                          <input
                            id={'County'}
                            className={"form-control"}
                            maxLength={50}
                            readOnly={true}
                            autoComplete={"off"}
                            type={"search"}
                            name={'County'}
                            aria-label={this.props.t("County")}
                            onChange={(e) => {
                              this.setState((prevState) => ({
                                selectedExemption: {
                                  ...prevState.selectedExemption,
                                  ParentCounty: e.target.value
                                }
                              }))
                            }}
                            value={this.state.selectedExemption?.ParentCounty}
                          />, this.props.t('County'), true
                        )}
                        {buildRow(this.props.t('Apartment/Suite/Building #'),
                          <input
                            id={'SecondaryAddressLine'}
                            className={"form-control"}
                            maxLength={50}
                            readOnly={true}
                            autoComplete={"off"}
                            type={"search"}
                            name={'SecondaryAddressLine'}
                            aria-label={this.props.t("Apartment/Suite/Building #")}
                            onChange={(e) => {
                              this.setState((prevState) => ({
                                selectedExemption: {
                                  ...prevState.selectedExemption,
                                  ParentAdditionalAddressInfo: e.target.value
                                }
                              }))
                            }}
                            value={this.state.selectedExemption?.ParentAdditionalAddressInfo}
                          />, this.props.t('Apartment/Suite/Building #')
                        )}
                        {buildRow(this.props.t("Phone"),
                          <input className={"form-control"}
                            maxLength={50}
                            readOnly={this.state.selectedExemption?.ID != null}
                            autoComplete={"off"}
                            type={"search"}
                            name={"Result"}
                            aria-label={this.props.t("Parent/Guardian Phone")}
                            value={this.state.selectedExemption?.ParentPhone}
                          />
                        )}
                        {buildRow(this.props.t("Email"),
                          <input className={"form-control"}
                            maxLength={50}
                            readOnly={this.state.selectedExemption?.ID != null}
                            autoComplete={"off"}
                            type={"search"}
                            name={"Result"}
                            aria-label={this.props.t("Parent/Guardian Email")}
                            value={this.state.selectedExemption?.ParentEmail}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/*School */}
                <div className="card mb-2">
                  <button className="card-header verlag-bold accordionButton w-100" data-toggle="collapse" data-target="#schoolXLCollapse" onClick={() => this.setState({ schoolArrowDown: !this.state.schoolArrowDown, })} aria-expanded="false" aria-controls="schoolXLCollapse">
                    <div className="row justify-content-between px-3">
                      <div className="">
                        <h4>{this.props.t("School")}</h4>
                      </div>
                      <div className="col-2">
                        {this.state.schoolArrowDown ? (
                          <AiOutlineUp className={"float-right"} size={30} data-toggle="collapse" data-target="#schoolXLCollapse" role="button" aria-expanded="false" aria-controls="schoolXLCollapse" />
                        ) : (
                          <AiOutlineDown className={"float-right"} size={30} data-toggle="collapse" data-target="#schoolXLCollapse" role="button" aria-expanded="false" aria-controls="schoolXLCollapse" />
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="card-body collapse" id={"schoolXLCollapse"}>
                    <div className="row">
                      <div className="col-12">
                        {this.state.selectedExemption.EducationSite?.map((site, index) => (
                          <section key={site._key} className='border rounded py-2 px-4 mb-3' style={{ borderColor: "rgba(0, 0, 0, 0.125)" }}>
                            {this.state.selectedExemption.EducationSite.length > 1 ?
                              <div className="row justify-content-end">
                                <div className="d-flex justify-content-center align-content-center">
                                  <button style={{ outline: 'none' }} type="button" className="close"
                                    aria-label={this.props.t("Remove School/ Child Care")} onClick={() => this.handleRemoveEducationSite(index)}>
                                    <span aria-hidden="true" className='d-inline-block pb-3 mr-3' style={{ fontSize: '1.5em', fontWeight: 'bold', color: "red" }}>&times;</span>
                                  </button>
                                </div>
                              </div> : <></>}
                            {buildRow(this.props.t('School/ Child Care'),
                              <Select
                                key={'School/Childcare'}
                                id={'School/Childcare'}
                                isClearable={true}
                                isSearchable={true}
                                placeholder={this.props.t("Please Select...")}
                                noOptionsMessage={() => this.props.t("No option")}
                                aria-label="School or Child Care"
                                value={site.schoolOrChildCare !== null ? getLabel(site.schoolOrChildCare, [{ label: "School", value: 0 }, { label: "Child Care", value: 1 }], this.props.t) : null}
                                onChange={(e) => {
                                  this.setState((prevState) => {
                                    let schoolArray = (prevState.selectedExemption.EducationSite || []).map(s => ({ ...s }));
                                    schoolArray[index] = { schoolOrChildCare: e?.value !== undefined ? e.value : null, SchoolDistrict: null, School: null, ChildCareSite: null, SchoolGrade: null, SchoolPhone: null };
                                    return {
                                      selectedExemption: { ...prevState.selectedExemption, EducationSite: schoolArray }
                                    };
                                  });
                                }}
                                className={'state_select'}
                                options={[{ label: this.props.t("School"), value: 0 }, { label: this.props.t("Child Care"), value: 1 }].filter(opt => {
                                  const otherSites = this.state.selectedExemption.EducationSite.filter((_, i) => i !== index);
                                  return !otherSites.some(s => s.schoolOrChildCare === opt.value);
                                })}
                              />, 'School/ Child Care', true
                            )}
                            {site?.schoolOrChildCare === 0 ? <>
                              {buildRow(this.props.t('Name of School'),
                                <Select
                                  key={'SchoolName'}
                                  id={'SchoolName'}
                                  isSearchable={true}
                                  isClearable={true}
                                  placeholder={this.props.t("Please Select...")}
                                  noOptionsMessage={() => this.props.t("No option")}
                                  aria-label={this.props.t("School Name")}
                                  value={site.School ? this.resolveSchoolLabel(site.School, site.isFacilityID) : null}
                                  onChange={(e) => this.handleUpdateSchool(index, "School", e?.value)}
                                  className={'state_select'}
                                  options={site.SchoolDistrict ? (this.state.facilitySchools || []).filter((f) => {
                                    return f.SchoolDistrictID === site.SchoolDistrict && f.ID !== -1
                                  }).map((f) => {
                                    return { label: f.FacilityName, value: f.ID }
                                  }) : (this.state.facilitySchools || []).filter((f) => f.ID !== -1).map((f) => {
                                    return { label: f.FacilityName, value: f.ID }
                                  })}
                                />, 'Name Of School', true
                              )}
                              {buildRow(this.props.t('School District'),
                                <Select
                                  key={'SchoolDistrict'}
                                  id={'SchoolDistrict'}
                                  isSearchable={true}
                                  isClearable={true}
                                  placeholder={this.props.t("Please Select...")}
                                  noOptionsMessage={() => this.props.t("No option")}
                                  aria-label={this.props.t("School District")}
                                  value={site?.SchoolDistrict ? getLabel(site.SchoolDistrict, this.state.districts) : null}
                                  onChange={(e) => this.handleUpdateSchool(index, "SchoolDistrict", e?.value ? e.value : null)}
                                  className={'state_select'}
                                  options={(this.state.districts || []).filter((d) => d.value !== -1)}
                                />, 'School District', true
                              )}
                            </> : site.schoolOrChildCare === 1 ?
                              buildRow(this.props.t('Name of Child Care'),
                                <Select
                                  key={'ChildCareSite'}
                                  id={'ChildCareSite'}
                                  isSearchable={true}
                                  isClearable={true}
                                  placeholder={this.props.t("Please Select...")}
                                  noOptionsMessage={() => this.props.t("No option")}
                                  aria-label={this.props.t("Name of Child Care")}
                                  value={site.ChildCareSite ?
                                    this.resolveChildcareLabel(site.ChildCareSite, site.isFacilityID) : null}
                                  onChange={(e) => this.handleUpdateSchool(index, "ChildCareSite", e?.value)}
                                  className={'state_select'}
                                  options={(this.state.facilityChildcares || []).map((f) => ({ label: f.FacilityName, value: f.ID }))}
                                />, 'Name Of Child Care', true
                              ) : <></>
                            }
                            {site.ChildCareSite === -1 ?
                              buildRow(this.props.t('Other Name of Child Care'),
                                <input
                                  id={'OtherChildCareSite'}
                                  className={"form-control"}
                                  maxLength={50}
                                  autoComplete={"off"}
                                  type={"search"}
                                  name={'OtherChildCareSite'}
                                  aria-label={this.props.t("Other Name of Child Care")}
                                  onChange={(e) => this.handleUpdateSchool(index, "OtherChildCareSite", e?.target?.value)}
                                  value={site?.OtherChildCareSite}
                                />, 'Other Name of Child Care', true
                              ) : <></>}
                            {site.schoolOrChildCare === 0 && buildRow(this.props.t('Grade'),
                              <Select
                                key={'SchoolGrade'}
                                id={'SchoolGrade'}
                                isSearchable={true}
                                isClearable={true}
                                placeholder={this.props.t("Please Select...")}
                                noOptionsMessage={() => this.props.t("No option")}
                                aria-label="School Grade"
                                value={site.SchoolGrade ? getLabel(site.SchoolGrade, this.state.gradeOptions, this.props.t) : null}
                                onChange={(e) => this.handleUpdateSchool(index, "SchoolGrade", e?.value)}
                                className={'state_select'}
                                options={this.state.gradeOptions}
                              />, 'School Grade of child', true
                            )}
                            {site.schoolOrChildCare === 0 ?
                              buildRow(this.props.t('School Phone'),
                                <PhoneInput
                                  id={'SchoolPhone'}
                                  placeholder={this.props.t("Enter phone number")}
                                  defaultCountry="US"
                                  aria-label={this.props.t('School Phone')}
                                  value={site.SchoolPhone}
                                  onChange={(val) => this.handleUpdateSchool(index, "SchoolPhone", val)} />
                              ) : site.schoolOrChildCare === 1 ?
                                buildRow(this.props.t('Child Care Phone'),
                                  <PhoneInput
                                    id={'ChildcarePhone'}
                                    placeholder={this.props.t("Enter phone number")}
                                    defaultCountry="US"
                                    aria-label={this.props.t('Child Care Phone')}
                                    value={site.SchoolPhone}
                                    onChange={(val) => this.handleUpdateSchool(index, "SchoolPhone", val)} />
                                ) : <></>
                            }</section>))}
                        {this.canAddMore() && <button className={"btn btn-success"} onClick={() => this.handleAddEducationSite()}>{this.props.t("Add School / Child Care")}</button>}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
              <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                <p className='d-inline-block m-0'><section className="text-danger d-inline-block px-1">*</section> {this.props.t('Required Fields')}</p>
                {this.state.selectedExemption?.ID && (
                  <button
                    className="btn btn-primary"
                    onClick={this.saveEdits}
                  >
                    {this.props.t("Save")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}


export default withTranslation()(ExemptionRecordModal);